import React, { useEffect, useRef, useState } from "react";
import { ProductId } from "./product";
import { Icon, Button } from "../components/tailwind";
import { getProduct, updateProductParent } from "../api/action";
import { useSelector } from "react-redux";
import { RootState, store } from "../utils/store";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../api/djangoToastSlice";

type Props = {
  customerSlug: string;
  productId: ProductId;
};

export const ProductDetailEditParentButton: React.FC<Props> = ({
  customerSlug,
  productId,
}) => {
  // Store the external id of the parent product
  const [currentParentEntity, setCurrentParentEntity] = useState("");
  // Store the value entered by the user
  const [parentEntity, setParentEntity] = useState("");
  // Current product
  const [product, setProduct] = useState(null);
  const mounted = useRef(true);
  const token = useSelector((state: RootState) => state.auth.token);

  useEffect(() => {
    (async (): Promise<void> => {
      mounted.current = true;
      if (mounted.current) {
        const product = await getProduct(token, productId);
        const entity = product?.parent_external_id || product?.parent_id || "";
        setCurrentParentEntity(entity);
        setParentEntity(entity);
        setProduct(product);
      }
    })();
    return (): void => {
      mounted.current = false;
    };
  }, []);

  const onProductParentChange = async (value: string): Promise<void> => {
    if (!mounted.current) {
      return;
    }
    const response = await updateProductParent({
      parentProductEntity: value,
      productId: product.id,
      token,
    });
    const djangoMessage = {
      appearance: NotificationAppearance.SUCCESS,
      content: "",
    };
    if (response.result) {
      setProduct(response.product);
      setCurrentParentEntity(value);
      setParentEntity(value);
    } else {
      djangoMessage.appearance = NotificationAppearance.ERROR;
    }
    djangoMessage.content = response.message;
    store.dispatch(setDjangoToastOpen(djangoMessage));
  };

  return (
    <div
      data-testid={"product-detail-edit-parent-button"}
      className="tw-flex tw-flex-col"
    >
      <label htmlFor="parent-product" className="tw-col-span-full">
        Parent product
      </label>
      <div className="tw-flex tw-items-stretch">
        {currentParentEntity && (
          <a
            data-balloon="Go to parent product"
            data-balloon-pos="up-right"
            className="txu-btn txu-compact-very txu-secondary-alt tw-flex tw-items-center !tw-border-r-0 !tw-rounded-r-none"
            data-testid={"product-detail-edited-parent-button-go-to-parent"}
            href={`/d/${customerSlug}/product/${product?.parent_id}?autologin=${token}`}
            data-subpage={"true"}
            target={"true"}
          >
            <Icon name="open_in_new" />
          </a>
        )}
        <input
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setParentEntity(event.target.value)
          }
          value={parentEntity || ""}
          placeholder="Enter parent SKU"
          id="parent-product"
          className={` txu-input tw-flex-1 ${
            currentParentEntity ? "!tw-rounded-none" : "!tw-rounded-r-none"
          }`}
          type="text"
        />

        <Button
          className="!tw-rounded-l-none !tw-rounded-r-none !tw-border-l-0"
          size="sm"
          compact
          content={currentParentEntity ? "Change parent" : "Add parent"}
          data-testid={
            "product-detail-edited-parent-button-parent-change-button"
          }
          disabled={parentEntity == currentParentEntity}
          onClick={(): Promise<void> => {
            if (!mounted.current) {
              return;
            }
            return onProductParentChange(parentEntity);
          }}
          type="button"
        />
        <Button
          data-balloon="Remove parent"
          data-balloon-pos="up-right"
          className="!tw-border-l-0 !tw-rounded-l-none"
          size="sm"
          icon
          compact
          data-testid={
            "product-detail-edited-parent-button-parent-remove-button"
          }
          disabled={!currentParentEntity}
          onClick={(): Promise<void> => {
            if (!mounted.current) {
              return;
            }
            return onProductParentChange("");
          }}
          type="button"
        >
          <Icon name="close" />
        </Button>
      </div>
    </div>
  );
};
